import * as Dfns from "date-fns/fp";

import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Brand50, Neutral200 } from "../../utils/colors";
import {
  OrderByOptions,
  isValidTimelineEntry,
  sortTimelineEntriesAlphabetically,
  sortTimelineEntriesByDateAsc,
  sortTimelineEntriesByDateDesc,
  truncateTimelineEntryDates,
} from "./OfflineInputsUtils";
import React, { useMemo, useState } from "react";

import { DateRange } from "../../utils/types";
import TimelineAxisRow from "./TimelineAxisRow";
import { TimelineEntry } from "@blisspointmedia/bpm-types/dist/OfflineInputs";
import cn from "classnames";
import { formatPercent } from "../../utils/format-utils";

interface TimelineBarChartProps {
  timelineEntries: TimelineEntry[];
  timelineDateRange: DateRange;
  fieldPrettyName: string;
  orderBy: OrderByOptions;
  chartContainerRightActions: JSX.Element;
}

// how much of each row (bar) does the purple cell take up
const CELL_TO_BAR_RATIO = 0.4;

const CHART_LEFT_MARGIN = 16;
const X_AXIS_TICK_COMPONENT_HEIGHT = 24;
const X_AXIS_TICK_HORIZONTAL_PADDING = 4;
const X_AXIS_TICK_VERTICAL_OFFSET = 2;
const Y_AXIS_TICK_WIDTH = 496;

const TimelineBarChart: React.FC<TimelineBarChartProps> = ({
  timelineEntries,
  timelineDateRange,
  orderBy,
  fieldPrettyName,
  chartContainerRightActions,
}) => {
  const dateMin = Dfns.parseISO(timelineDateRange.start);
  const dateMax = Dfns.parseISO(timelineDateRange.end);
  const xAxisTickInterval = Dfns.eachMonthOfInterval({
    start: Dfns.startOfMonth(dateMin),
    end: Dfns.add({ months: 1 })(Dfns.startOfMonth(dateMax)),
  }).map(date => date.getTime());
  const timeMin = dateMin.getTime();
  const timeMax = dateMax.getTime();
  const [chartWidth, setChartWidth] = useState<number>(0);
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const [barHeight, setBarHeight] = useState<number>(0);

  const processedChartData = useMemo(() => {
    const filteredEntries = timelineEntries
      .filter(entry => isValidTimelineEntry(entry, dateMin, dateMax))
      .map(entry => truncateTimelineEntryDates(entry, dateMin, dateMax));
    const sortedEntries =
      orderBy === OrderByOptions.ALPHABETICAL
        ? sortTimelineEntriesAlphabetically(filteredEntries)
        : orderBy === OrderByOptions.DATE_DESCENDING
        ? sortTimelineEntriesByDateDesc(filteredEntries)
        : sortTimelineEntriesByDateAsc(filteredEntries);
    return sortedEntries.map(
      ({ promoName, specialOfferName, percentageDiscount, startDate, endDate }, index) => {
        const start = Dfns.parseISO(startDate).getTime();
        const end = Dfns.add({ days: 1 })(Dfns.parseISO(endDate)).getTime();
        const formattedSpecialOfferName = specialOfferName ?? "";
        const formattedPercentageDiscount = percentageDiscount
          ? formatPercent(percentageDiscount / 100, 0)
          : "";
        return {
          id: index,
          name: `${promoName} ${formattedSpecialOfferName} ${formattedPercentageDiscount}`,
          promo: promoName,
          specialOfferName: specialOfferName,
          percentageDiscount: percentageDiscount,
          startDate,
          endDate,
          timeRange: [start, end],
        };
      }
    );
  }, [timelineEntries, orderBy, dateMin, dateMax]);

  // and similarly, some slight hackery for x axis ticks
  const xAxisWidth = useMemo<number>(() => {
    return chartWidth - Y_AXIS_TICK_WIDTH - CHART_LEFT_MARGIN;
  }, [chartWidth]);

  const xAxisTickWidthByDay = useMemo<number>(() => {
    return (
      xAxisWidth /
      Dfns.differenceInDays(xAxisTickInterval[0])(xAxisTickInterval[xAxisTickInterval.length - 1])
    );
  }, [xAxisTickInterval, xAxisWidth]);

  // rechart tick mark type
  // note to adjust y, height values if you create new ticks
  const yAxisTick = ({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => {
    const { value: payloadName } = payload;
    const timelineEntry = processedChartData.find(entry => entry.name === payloadName);
    if (!timelineEntry) {
      return <></>;
    }
    return (
      <foreignObject
        x={x - Y_AXIS_TICK_WIDTH}
        y={y - barHeight / 2}
        width={Y_AXIS_TICK_WIDTH}
        height={barHeight}
        textAnchor="start"
      >
        <TimelineAxisRow
          id={timelineEntry.id}
          name={timelineEntry.name}
          promo={timelineEntry.promo}
          specialOffer={timelineEntry.specialOfferName || ""}
          percentageDiscount={timelineEntry?.percentageDiscount || null}
          startDate={Dfns.parseISO(timelineEntry.startDate)}
          endDate={Dfns.parseISO(timelineEntry.endDate)}
        />
      </foreignObject>
    );
  };

  const xAxisTick = ({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => {
    const { value: tickDate } = payload;
    const tickIntervalWidth = Dfns.getDaysInMonth(tickDate) * xAxisTickWidthByDay;
    return (
      <foreignObject
        x={x + X_AXIS_TICK_HORIZONTAL_PADDING}
        y={(y - X_AXIS_TICK_COMPONENT_HEIGHT) / 2}
        width={tickIntervalWidth - X_AXIS_TICK_HORIZONTAL_PADDING * 2}
        // bumping by 2 to display border
        height={X_AXIS_TICK_COMPONENT_HEIGHT + X_AXIS_TICK_VERTICAL_OFFSET}
      >
        <div className="xAxisTick">{Dfns.format("MMM ''yy")(tickDate)}</div>
      </foreignObject>
    );
  };

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      onResize={(width, height) => {
        setChartWidth(width);
      }}
    >
      <BarChart
        key={processedChartData.length}
        layout="vertical"
        data={processedChartData}
        margin={{ left: CHART_LEFT_MARGIN }}
        barCategoryGap={0}
        onMouseMove={state => {
          if (state.isTooltipActive) {
            setHoveredValue(state.activeLabel);
          } else {
            setHoveredValue(undefined);
          }
        }}
      >
        <XAxis
          dataKey="timeRange"
          scale="time"
          type="number"
          domain={[timeMin, timeMax]}
          tick={xAxisTick}
          ticks={xAxisTickInterval}
          interval={0}
          orientation="top"
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          formatter={timeRange => {
            return [
              `${new Date(timeRange[0]).toLocaleDateString()} - ${new Date(
                timeRange[1]
              ).toLocaleDateString()}`,
            ];
          }}
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={yAxisTick}
          tickLine={false}
          axisLine={false}
          width={Y_AXIS_TICK_WIDTH}
          interval={0}
        />
        <Bar
          dataKey="timeRange"
          fill={Brand50}
          background={props => {
            const { x, y, width, height, index } = props;
            if (index === 0) {
              setBarHeight(height);
            }
            return (
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={index % 2 === 0 ? Neutral200 : "#FFFFFF"}
              />
            );
          }}
          shape={props => {
            const { className, x, y, width, height, fill } = props;
            const cellHeight = height * CELL_TO_BAR_RATIO;
            const offsetY = (height - cellHeight) / 2;

            return (
              <rect
                className={className}
                x={x}
                y={y + offsetY}
                width={width}
                height={cellHeight}
                fill={fill}
              />
            );
          }}
        >
          {processedChartData.map((entry, index) => (
            <Cell
              className={cn("barCell", {
                hoveredValue: entry.name === hoveredValue,
                unhoveredValue: Boolean(hoveredValue) && entry.name !== hoveredValue,
              })}
              key={`cell-${index}`}
              fill={Brand50}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default TimelineBarChart;
